<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminPayerList" label="Payers" />
      <breadcrumb-link view="AdminPayerDetail" :label="payer?.name" v-if="payer" :active="true" />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>

      <div v-if="payer" class="content">
        <!-- Edit Button -->
        <div class="mb-4">
          <b-button
            type="is-primary"
            tag="router-link"
            :to="{ name: 'AdminPayerUpdate', params: { id: payer.id } }"
          >
            Edit Payer
          </b-button>
        </div>

        <!-- Payer Information Section -->
        <div class="section">
          <b-collapse class="card" :open="true" aria-id="payerInfo">
            <template #trigger="props">
              <div class="card-header" role="button" aria-controls="payerInfo">
                <p class="card-header-title">
                  <b-icon icon="information" class="mr-2"></b-icon>
                  Payer Information
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                </a>
              </div>
            </template>
            <div class="card-content">
              <div class="columns is-multiline">
                <div class="column is-6">
                  <p class="detail-item">
                    <span class="detail-label">ID</span>
                    <span class="detail-value">{{ payer.id }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Name</span>
                    <span class="detail-value">{{ payer.name }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Internal Name</span>
                    <span class="detail-value">{{ payer.name_internal }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Code</span>
                    <span class="detail-value">{{ payer.code }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Type</span>
                    <span class="detail-value">
                      <span class="tag" :class="payer.is_blue ? 'is-info' : 'is-light'">
                        {{ payer.is_blue ? 'Blue' : 'Non-Blue' }}
                      </span>
                    </span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Pverify Name</span>
                    <span class="detail-value">{{ payer.pverify_name }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Pverify Code</span>
                    <span class="detail-value">{{ payer.pverify_code }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Pverify Status</span>
                    <span class="detail-value">
                      <span
                        class="tag"
                        :class="payer.pverify_is_active ? 'is-success' : 'is-danger'"
                      >
                        {{ payer.pverify_is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </span>
                  </p>
                </div>
                <div class="column is-6">
                  <p class="detail-item">
                    <span class="detail-label">Status</span>
                    <span class="detail-value">
                      <span class="tag" :class="payer.is_active ? 'is-success' : 'is-danger'">
                        {{ payer.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Is Group</span>
                    <span class="detail-value">
                      <span class="tag" :class="payer.is_group ? 'is-info' : 'is-light'">
                        {{ payer.is_group ? 'Yes' : 'No' }}
                      </span>
                    </span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Available</span>
                    <span class="detail-value">
                      <span class="tag" :class="payer.is_available ? 'is-success' : 'is-light'">
                        {{ payer.is_available ? 'Yes' : 'No' }}
                      </span>
                    </span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Created At</span>
                    <span class="detail-value">{{ formatDate(payer.created_at) }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Updated At</span>
                    <span class="detail-value">{{ formatDate(payer.updated_at) }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Deleted At</span>
                    <span class="detail-value">{{ formatDate(payer.deleted_at) }}</span>
                  </p>
                  <p class="detail-item">
                    <span class="detail-label">Parent ID</span>
                    <span class="detail-value">{{ payer.parent_id || 'N/A' }}</span>
                  </p>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>

        <!-- Children Response Section -->
        <div class="section">
          <b-collapse class="card" :open="false" aria-id="childrenResponse">
            <template #trigger="props">
              <div class="card-header" role="button" aria-controls="childrenResponse">
                <p class="card-header-title">
                  <b-icon icon="folder" class="mr-2"></b-icon>
                  Children Response
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                </a>
              </div>
            </template>
            <div class="card-content">
              <pre class="response-box">{{ JSON.stringify(payer.children || {}, null, 2) }}</pre>
            </div>
          </b-collapse>
        </div>

        <!-- Schema Section -->
        <div class="section">
          <b-collapse class="card" :open="false" aria-id="schemaDetails">
            <template #trigger="props">
              <div class="card-header" role="button" aria-controls="schemaDetails">
                <p class="card-header-title">
                  <b-icon icon="file-document" class="mr-2"></b-icon>
                  Schema Details
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                </a>
              </div>
            </template>
            <div class="card-content">
              <pre class="response-box">{{ JSON.stringify(schema || {}, null, 2) }}</pre>
            </div>
          </b-collapse>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminPayerDetail',
  components: {
    AdminLayout,
  },
  data() {
    return {
      payer: null,
      schema: null,
      loading: true,
    };
  },
  created() {
    request
      .get(`/admin/payers/${this.$route.params.id}`)
      .then((resp) => {
        this.payer = resp.data.payer;
        this.schema = resp.data.schema;
      })
      .finally(() => (this.loading = false));
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },
  },
};
</script>

<style scoped>
.section {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section:last-child {
  margin-bottom: 0;
}

.card {
  margin-bottom: 0;
  box-shadow: 0 1px 1px rgba(10, 10, 10, 0.03);
  border-radius: 2px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 1px 1px rgba(10, 10, 10, 0.08);
}

.card-header {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-bottom: 1px solid #eee;
}

.card-header-title {
  font-weight: 600;
  color: #363636;
  font-size: 0.9rem;
}

.card-content {
  padding: 0.5rem;
}

.detail-item {
  margin-bottom: 0.25rem;
  padding: 0.25rem;
  background-color: #f8f9fa;
  border-radius: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 600;
  color: #4a4a4a;
  min-width: 150px;
  font-size: 0.9rem;
}

.detail-value {
  color: #363636;
  text-align: right;
  flex: 1;
  margin-left: 0.75rem;
  font-size: 0.9rem;
}

.response-box {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 2px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Fira Code', monospace;
  font-size: 0.8rem;
  line-height: 1.3;
  border: 1px solid #eee;
}

.tag {
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  font-size: 0.8rem;
}

.mb-4 {
  margin-bottom: 0.5rem;
}

.ml-2 {
  margin-left: 0.3rem;
}

.mr-2 {
  margin-right: 0.3rem;
}
</style>
